import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getDealReplaceApiUrl } from '../utils/apiUrls';
import {
  clearStoredDealReplaceParams,
  dealReplaceCopy,
  dealReplaceFileBlocks,
  searchWithDealReplaceParams,
  stripDealReplaceParams,
} from '../utils/dealReplaceConfirm';
import './DealReplaceConfirmModal.css';

const DealReplaceConfirmModal = ({ search, address, token, action, onClose }) => {
  const navigate = useNavigate();
  const resolved = token && action
    ? { params: { token, action }, search: search || '' }
    : searchWithDealReplaceParams(search);
  const params = resolved.params;
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [busy, setBusy] = useState(false);
  const [doneMessage, setDoneMessage] = useState('');
  const [wrongTarget, setWrongTarget] = useState(null);
  const [correctedAddress, setCorrectedAddress] = useState('');

  useEffect(() => {
    if (token || !params) {
      return undefined;
    }
    if (resolved.search && resolved.search !== (search || '')) {
      navigate({ search: resolved.search }, { replace: true });
    }
    return undefined;
  }, [navigate, params, resolved.search, search, token]);

  useEffect(() => {
    if (!params) {
      return undefined;
    }
    let cancelled = false;
    setPreviewError('');
    setSubmitError('');
    setDoneMessage('');
    setWrongTarget(null);
    setCorrectedAddress('');
    axios.get(`${getDealReplaceApiUrl()}/${params.token}/preview`)
      .then((response) => {
        if (!cancelled) {
          const data = response.data || {};
          setPreview(data);
          if (data.already_confirmed && data.resolved_message) {
            const retryWaiting = params.action === 'ready_retry'
              && data.status === 'awaiting_listing_reactivation';
            if (!retryWaiting) {
              setDoneMessage(data.resolved_message);
            }
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setPreviewError(err.response?.data?.message || 'This confirmation link is no longer valid.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [params?.token, params?.action]);

  if (!params || params.action === 'draft_yes' || params.action === 'draft_no') {
    return null;
  }

  const kind = (preview?.held_doc_type || '').startsWith('listing') ? 'listing' : 'contract';
  const displayAddress = preview?.existing_address
    || preview?.incoming_address_display
    || address
    || preview?.incoming_address
    || 'this property';
  const copy = dealReplaceCopy(params.action, { kind, address: displayAddress });
  const blocked = params.action === 'cancel_open_file' && preview?.cancel_allowed === false;
  const fileBlocks = dealReplaceFileBlocks(preview, kind).filter((block) => (
    block.key !== 'existing' || preview?.cancel_allowed !== false
  ));
  const selectedBlock = fileBlocks.find((block) => block.key === wrongTarget);
  const isWrongAddress = params.action === 'wrong_address';
  const isCancel = params.action === 'cancel_open_file';

  const closeModal = () => {
    clearStoredDealReplaceParams();
    if (onClose) {
      onClose(doneMessage);
      return;
    }
    navigate({ search: stripDealReplaceParams(search) }, { replace: true });
  };

  const confirm = async () => {
    setBusy(true);
    setSubmitError('');
    try {
      const body = { action: params.action };
      if (isWrongAddress) {
        body.target = wrongTarget;
        body.corrected_address = correctedAddress.trim();
      }
      const response = await axios.post(
        `${getDealReplaceApiUrl()}/${params.token}/resolve`,
        body,
        { headers: { Accept: 'application/json' } },
      );
      setDoneMessage(response.data?.message || 'Done.');
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Could not complete that action.');
    } finally {
      setBusy(false);
    }
  };

  const confirmDisabled = busy
    || Boolean(previewError)
    || blocked
    || (isWrongAddress && (!wrongTarget || !correctedAddress.trim()));

  return (
    <div className="deal-replace-modal-backdrop" onClick={closeModal} role="presentation">
      <div
        className="deal-replace-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={doneMessage ? undefined : 'deal-replace-modal-title'}
        aria-label={doneMessage ? 'Confirmation' : undefined}
      >
        {!doneMessage && (
          <div className="deal-replace-modal-header">
            <h2 id="deal-replace-modal-title">{copy.title}</h2>
            <button
              type="button"
              className="deal-replace-modal-close"
              onClick={closeModal}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}
        <div className="deal-replace-modal-body">
          {doneMessage ? (
            <p>{doneMessage}</p>
          ) : (
            <p>{copy.body}</p>
          )}
          {isCancel && !doneMessage && (
            <div className="deal-replace-file-card deal-replace-file-card-static">
              <strong>File to close</strong>
              <span>Address — {fileBlocks[0]?.address || displayAddress}</span>
              {kind === 'contract' && (
                <span>Buyers — {fileBlocks[0]?.buyers || 'Not listed'}</span>
              )}
              <span>Sellers — {fileBlocks[0]?.sellers || 'Not listed'}</span>
            </div>
          )}
          {isWrongAddress && !doneMessage && !wrongTarget && (
            <div className="deal-replace-file-list">
              {fileBlocks.map((block) => (
                <button
                  key={block.key}
                  type="button"
                  className="deal-replace-file-card"
                  onClick={() => {
                    setWrongTarget(block.key);
                    setCorrectedAddress(block.address === 'this property' ? '' : block.address);
                  }}
                >
                  <strong>{block.title}</strong>
                  <span>Address — {block.address}</span>
                  {kind === 'contract' && <span>Buyers — {block.buyers}</span>}
                  <span>Sellers — {block.sellers}</span>
                </button>
              ))}
            </div>
          )}
          {isWrongAddress && !doneMessage && selectedBlock && (
            <div className="deal-replace-file-card deal-replace-file-card-selected">
              <strong>{selectedBlock.title}</strong>
              <span>Address — {selectedBlock.address}</span>
              {kind === 'contract' && <span>Buyers — {selectedBlock.buyers}</span>}
              <span>Sellers — {selectedBlock.sellers}</span>
              <label className="deal-replace-address-label" htmlFor="deal-replace-corrected-address">
                Corrected address
              </label>
              <input
                id="deal-replace-corrected-address"
                className="deal-replace-address-input"
                type="text"
                value={correctedAddress}
                onChange={(event) => setCorrectedAddress(event.target.value)}
                placeholder="Street, city, state, ZIP"
                autoFocus
              />
            </div>
          )}
          {!isWrongAddress && !isCancel && preview && (
            <p className="deal-replace-modal-meta">
              <strong>Property:</strong> {displayAddress}
            </p>
          )}
          {(previewError || blocked) && (
            <p className="deal-replace-modal-error">
              {previewError || 'You do not have access to cancel this file.'}
            </p>
          )}
          {submitError && <p className="deal-replace-modal-error">{submitError}</p>}
        </div>
        <div className="deal-replace-modal-actions">
          {isWrongAddress && selectedBlock && !doneMessage && (
            <button
              type="button"
              className="deal-replace-secondary-button"
              onClick={() => {
                setWrongTarget(null);
                setCorrectedAddress('');
                setSubmitError('');
              }}
            >
              Back
            </button>
          )}
          <button
            type="button"
            className="deal-replace-secondary-button"
            onClick={closeModal}
          >
            {doneMessage ? 'Close' : 'Not now'}
          </button>
          {!doneMessage && (!isWrongAddress || selectedBlock) && (
            <button
              type="button"
              className="deal-replace-primary-button"
              onClick={confirm}
              disabled={confirmDisabled}
            >
              {busy ? 'Working…' : copy.confirm}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealReplaceConfirmModal;
